import Dexie, { Table } from 'dexie';
import { TableFormat } from './types';

export interface CachedDataTable {
  datatableId: string; // datatable id
  datasetId: string; // dataset this table belongs to
  version: number; // default: 1
  fileName: string;
  format: TableFormat;
  size: number;
  fileData: Uint8Array;
  cachedAt: Date;
  cacheLastAccessedAt: Date;
}

interface CacheConfig {
  maxSize: number; // default: 500MB
  maxAge: number; // default: 7 days
}

class DataTableCacheDB extends Dexie {
  cachedTables!: Table<CachedDataTable, string>;

  constructor() {
    super('datatable-cache');
    
    this.version(1).stores({
      cachedTables: 'datatableId, cacheLastAccessedAt'
    });

    this.version(2).stores({
      cachedTables: 'datatableId, cacheLastAccessedAt'
    });
  }
}

/**
 * Cache for data tables, primarily used for Parquet files to avoid
 * slow CREATE TABLE AS SELECT operations in DuckDB WASM
 */
export class DataTableCache {
  private db: DataTableCacheDB;
  
  private config: CacheConfig = {
    maxSize: 500 * 1024 * 1024, // 500MB
    maxAge: 7 * 24 * 60 * 60 * 1000, // 7 days
  };

  constructor() {
    this.db = new DataTableCacheDB();
  }

  /**
   * Get a cached data table by ID
   * Automatically updates the last accessed timestamp
   */
  async get(dataTableId: string): Promise<CachedDataTable | null> {
    try {
      const result = await this.db.cachedTables.get(dataTableId);
      if (result) {
        // Automatically update last accessed time
        await this.updateLastAccessed(dataTableId);
        return result;
      }
      return null;
    } catch (error) {
      console.error('Failed to get cached table:', error);
      return null;
    }
  }

  /**
   * Store a cached data table
   */
  async store(table: CachedDataTable): Promise<void> {
    try {
      // Check if we're overwriting existing data
      const existing = await this.db.cachedTables.get(table.datatableId);
      const isUpsert = existing !== undefined;
      
      // Store the data
      await this.db.cachedTables.put(table);
      

    } catch (error) {
      throw new Error(`Failed to store cached table: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Delete a cached data table
   */
  async delete(dataTableId: string): Promise<void> {
    try {
      await this.db.cachedTables.delete(dataTableId);
    } catch (error) {
      throw new Error(`Failed to delete cached table: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Download and cache a data table from URL
   */
  async downloadAndCache(params: {
    dataTableId: string;
    datasetId: string;
    url: string;
    format: TableFormat;
    version?: number;
  }): Promise<CachedDataTable> {
    const { dataTableId, datasetId, url, format, version = 1 } = params;

    try {
      // Download the file
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const fileData = new Uint8Array(arrayBuffer);
      
      // Extract filename from URL
      const fileName = this.extractFileName(url, format);

      const cachedTable: CachedDataTable = {
        datatableId: dataTableId,
        datasetId,
        version,
        fileName,
        format,
        size: fileData.length,
        fileData,
        cachedAt: new Date(),
        cacheLastAccessedAt: new Date(),
      };

      // Store in cache
      await this.store(cachedTable);

      // Check if we need to cleanup
      await this.cleanupIfNeeded();

      return cachedTable;
    } catch (error) {
      throw new Error(`Failed to download and cache file: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Get all cached tables for a dataset
   */
  async getByDatasetId(datasetId: string): Promise<CachedDataTable[]> {
    try {
      return await this.db.cachedTables.where('datasetId').equals(datasetId).toArray();
    } catch (error) {
      console.error('Failed to get tables by dataset ID:', error);
      return [];
    }
  }

  /**
   * Update the last accessed timestamp for a cached table
   */
  async updateLastAccessed(dataTableId: string): Promise<void> {
    try {
      await this.db.cachedTables.update(dataTableId, {
        cacheLastAccessedAt: new Date()
      });
    } catch (error) {
      console.error('Failed to update last accessed:', error);
    }
  }

  /**
   * Clean up old cached tables based on age
   */
  async cleanup(maxAge: number): Promise<void> {
    const cutoffDate = new Date(Date.now() - maxAge);
    
    try {
      await this.db.cachedTables
        .where('cacheLastAccessedAt')
        .below(cutoffDate)
        .delete();
    } catch (error) {
      console.error('Failed to cleanup old tables:', error);
    }
  }

  /**
   * Clean up cached tables to stay under size limit (LRU)
   */
  async cleanupBySize(maxSizeBytes: number): Promise<void> {
    try {
      // Get all tables ordered by last accessed (oldest first)
      const allTables = await this.db.cachedTables
        .orderBy('cacheLastAccessedAt')
        .toArray();

      let totalSize = allTables.reduce((sum, table) => sum + table.size, 0);

      // Remove oldest tables until we're under the limit
      for (const table of allTables) {
        if (totalSize <= maxSizeBytes) break;
        
        await this.db.cachedTables.delete(table.datatableId);
        totalSize -= table.size;
      }
    } catch (error) {
      console.error('Failed to cleanup by size:', error);
    }
  }

  /**
   * Get total size of all cached data
   */
  async getSize(): Promise<number> {
    try {
      const allTables = await this.db.cachedTables.toArray();
      return allTables.reduce((sum, table) => sum + table.size, 0);
    } catch (error) {
      console.error('Failed to get cache size:', error);
      return 0;
    }
  }

  /**
   * Clear all cached data
   */
  async clear(): Promise<void> {
    try {
      await this.db.cachedTables.clear();
    } catch (error) {
      throw new Error(`Failed to clear cache: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Cleanup if needed based on config
   */
  private async cleanupIfNeeded(): Promise<void> {
    try {
      // Cleanup by age
      await this.cleanup(this.config.maxAge);
      
      // Cleanup by size
      await this.cleanupBySize(this.config.maxSize);
    } catch (error) {
      console.error('Failed to perform automatic cleanup:', error);
    }
  }

  /**
   * Extract filename from URL, with fallback based on format
   */
  private extractFileName(url: string, format: TableFormat): string {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = pathname.split('/').pop();
      
      if (filename && filename.includes('.')) {
        return filename;
      }
      
             // Fallback: generate filename based on format
       const timestamp = Date.now();
       const extensions: Record<TableFormat, string> = {
         CSV: 'csv',
         TSV: 'tsv', 
         JSON: 'json',
         PARQUET: 'parquet',
         ARROW: 'arrow'
       };
       
       return `data_${timestamp}.${extensions[format]}`;
    } catch {
             // If URL parsing fails, generate a filename
       const timestamp = Date.now();
       const extensions: Record<TableFormat, string> = {
         CSV: 'csv',
         TSV: 'tsv',
         JSON: 'json', 
         PARQUET: 'parquet',
         ARROW: 'arrow'
       };
       
       return `data_${timestamp}.${extensions[format]}`;
    }
  }

  /**
   * Close the database connection
   */
  async close(): Promise<void> {
    try {
      await this.db.close();
    } catch (error) {
      console.error('Failed to close database:', error);
    }
  }
} 